/**
 * telemetrySDK.js — PostHog bridge.
 *
 * # What this file does
 * Translates our framework-free in-app telemetry shim (the
 * `window.dispatchEvent('xpert:telemetry', {detail: {name, props, ts}})`
 * pattern in `telemetry.js`) into PostHog `capture()` calls.
 *
 * # Why a bridge and not direct calls
 * Three reasons:
 *
 *   1. Every existing call site already uses `track(name, props)` from
 *      `telemetry.js`. Migrating each to `posthog.capture` would touch
 *      dozens of files. The bridge keeps all the call sites unchanged
 *      and forwards their events to PostHog at one place.
 *
 *   2. The shim's `xpert:telemetry` CustomEvent is a stable contract
 *      pinned by tests (`discoveryTelemetry.test.js`,
 *      `getVerifiedV2Telemetry.test.js`). If we ever swap PostHog for
 *      Mixpanel / Segment / a self-hosted collector, we replace this
 *      one file — never touch the call sites.
 *
 *   3. Tests that exercise the bridge can mock PostHog cleanly without
 *      having to mock every page-level emitter.
 *
 * # Env contract
 * `REACT_APP_POSTHOG_KEY`  — PostHog project key. When unset / empty,
 *                            the SDK is NOT initialised and the bridge
 *                            silently no-ops. This is the same gate
 *                            Sentry uses (see index.js). Means dev + CI
 *                            run cleanly without leaking events to a
 *                            real project.
 * `REACT_APP_POSTHOG_HOST` — optional API host. Defaults to
 *                            https://us.i.posthog.com (PostHog Cloud US).
 *                            Set to https://eu.i.posthog.com for EU
 *                            data residency, or your own URL for
 *                            self-hosted.
 *
 * # Privacy posture
 *   - `disable_session_recording: true` — replay is opt-in via PostHog
 *     dashboard, never sent from this file. When the project owner
 *     decides to enable replay, that's a deliberate dashboard toggle.
 *   - `autocapture: false` — we instrument explicitly via the shim.
 *     Autocapture would double-count clicks AND capture text content
 *     from inputs the user didn't expect.
 *   - `respect_dnt: true` — honour the Do Not Track browser signal.
 *   - `mask_all_text: true` AND `mask_all_element_attributes: true` —
 *     belt-and-braces for the day session recording is enabled.
 *   - We forward only the `props` object we explicitly set in call
 *     sites. PII discipline (no raw search queries, no password
 *     fields, etc.) is pinned by the per-surface telemetry tests.
 *
 * # Lifecycle
 *   `initTelemetry()`   — call exactly once at app bootstrap.
 *                          Idempotent: subsequent calls are no-ops.
 *                          Returns a teardown function (used by tests).
 *   `identifyUser(u)`   — call after a successful login/register/
 *                          fetchUser. Stitches anonymous pre-auth
 *                          events to the now-known user. Sends only the
 *                          user id and `user_type`, nothing else.
 *   `resetIdentity()`   — call on logout. Generates a new anonymous_id
 *                          for the next session on this browser.
 */
import posthog from "posthog-js";

// Module-level guard so multiple init calls don't double-install the
// bridge listener (React StrictMode mounts effects twice in dev).
let _initialized = false;
let _bridgeListener = null;

/**
 * Initialise PostHog if the env var is set. Idempotent.
 *
 * @returns {() => void} teardown function — removes the bridge
 *   listener and marks the module re-initialisable. Used by tests; in
 *   production the SDK runs for the lifetime of the page.
 */
export function initTelemetry() {
  if (_initialized) return _teardown;

  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) {
    // No key → SDK not initialised. Events still dispatch via the
    // existing `track()` shim — they just have no destination, which
    // is the pre-PostHog behaviour. Logging at init keeps the silent
    // no-op discoverable without spamming dev consoles.
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug(
        "[telemetrySDK] REACT_APP_POSTHOG_KEY not set — bridge inactive",
      );
    }
    _initialized = true;
    return _teardown;
  }

  const host =
    process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

  posthog.init(key, {
    api_host: host,

    // Pageview tracking — PostHog auto-fires $pageview on init + on
    // history.pushState. Essential for funnel analysis (landing →
    // experts → profile). The Sentry browserTracingIntegration also
    // tracks navigations but for perf, not funnels — different purposes.
    capture_pageview: true,
    capture_pageleave: true,    // duration-on-page metric

    // We instrument explicitly via the shim — see "Privacy posture"
    // above for why this is off.
    autocapture: false,

    // Privacy defaults — see telemetrySDK.js header for full rationale.
    disable_session_recording: true,
    respect_dnt: true,
    mask_all_text: true,
    mask_all_element_attributes: true,

    // GDPR/ePrivacy: start OPTED-OUT. Nothing (not even the init $pageview)
    // is captured until the user grants the "analytics" category in the
    // cookie banner, which calls setTelemetryConsent(). Previously PostHog
    // captured pageviews on load regardless of the banner choice — the
    // banner was decorative. A prior "accept" is honoured below on init.
    opt_out_capturing_by_default: true,

    // Use localStorage (not cookies) so anonymous identity survives
    // Safari ITP / third-party cookie blocks. Single-domain app, so
    // there's no cross-site stitching to worry about.
    persistence: "localStorage",

    // Don't auto-load the toolbar in dev (we don't need it; it can
    // be enabled per-session via PostHog dashboard if desired).
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.debug("[telemetrySDK] PostHog ready", { host });
        ph.debug(false);
      }
    },
  });

  // Bridge: forward every shim CustomEvent to posthog.capture.
  //
  // `send_instantly: true` bypasses PostHog's default batching queue
  // and POSTs the event immediately. Critical for events fired right
  // before a navigation — e.g. `getverified_signin_link_clicked`
  // (followed by <Link> nav to /login) or `getverified_account_method_chosen`
  // (followed by `window.location.href = ...` to the Google OAuth
  // endpoint). With batching, the browser unloads the page before the
  // batch flushes and the event is lost; we noticed this on the first
  // day of PostHog data — the SignInLink + OAuth events never arrived
  // even when the user demonstrably clicked them.
  //
  // The bandwidth cost (one HTTP request per event instead of batched
  // ~5/req) is microscopic at our event volume. We can re-introduce
  // batching once we have enough traffic to make per-event request
  // overhead meaningful — at which point we'd move send_instantly to
  // an opt-in flag set only by call sites that navigate.
  _bridgeListener = (e) => {
    try {
      const detail = e?.detail;
      if (!detail || typeof detail.name !== "string") return;
      posthog.capture(detail.name, detail.props || {}, { send_instantly: true });
    } catch {
      // Telemetry must never throw — swallow and move on.
    }
  };
  window.addEventListener("xpert:telemetry", _bridgeListener);

  // Honour a prior analytics-consent choice from the cookie banner. New
  // visitors stay opted-out (see opt_out_capturing_by_default above) until
  // they accept; the banner then calls setTelemetryConsent() live. We read
  // localStorage directly (not window.__xpertCookieChoice) because init runs
  // at bootstrap before the banner component mounts.
  try {
    if (_readAnalyticsConsent() === true) posthog.opt_in_capturing();
  } catch {
    /* stay opted out */
  }

  _initialized = true;
  return _teardown;
}

/**
 * Read the stored analytics-cookie consent, if any.
 * @returns {boolean|null} true = granted, false = denied, null = no choice yet.
 */
function _readAnalyticsConsent() {
  try {
    const live = typeof window !== "undefined" ? window.__xpertCookieChoice : null;
    const raw = live || JSON.parse(localStorage.getItem("xpert_cookie_choice") || "null");
    return raw ? !!raw.analytics : null;
  } catch {
    return null;
  }
}

/**
 * Apply an analytics-consent decision from the cookie banner. Called on
 * accept/reject. opt_in flushes queued events + resumes capture; opt_out
 * stops all capture and persists the decision in PostHog's own storage.
 *
 * @param {boolean} analyticsGranted
 */
export function setTelemetryConsent(analyticsGranted) {
  if (!_initialized) return;
  try {
    if (analyticsGranted) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  } catch {
    /* swallow — telemetry must never throw */
  }
}

/**
 * Identify the current user so subsequent + retroactive anonymous
 * events stitch to them. Idempotent for the same user (PostHog handles
 * repeat identifies). Safe to call before init — becomes a no-op.
 *
 * Privacy: only the opaque user id and the account type (`user_type`) are
 * ever sent. Email, name, role, country and signup date are NOT. Product
 * analytics does not need them, and every one we send is personal data we
 * would have to disclose and honour deletion for in PostHog. (CEO decision,
 * "Option B", from the privacy review in docs/office/PRIVACY_ADDENDUM_DRAFT.md.)
 *
 * @param {object} user  must have a string `id`. Only `user_type` (a string)
 *                       is read from the rest of the object.
 */
export function identifyUser(user) {
  if (!_initialized) return;
  if (!user || typeof user.id !== "string") return;

  // Allow-list of exactly one trait. We deliberately do NOT pass the user
  // object through, so a field added to the User model later can never
  // start flowing to analytics by accident.
  const traits = {};
  if (typeof user.user_type === "string" && user.user_type) {
    traits.user_type = user.user_type;
  }

  try {
    posthog.identify(String(user.id), traits);
  } catch {
    /* swallow */
  }
}

/**
 * Reset identity — call on logout. PostHog clears the distinct_id and
 * generates a fresh anonymous_id so the next session on this browser
 * isn't attributed to the previous user.
 */
export function resetIdentity() {
  if (!_initialized) return;
  try {
    posthog.reset();
  } catch {
    /* swallow */
  }
}

// Teardown — used by tests. In production, the listener lives for the
// lifetime of the page and the GC reclaims it on unload.
function _teardown() {
  if (_bridgeListener) {
    window.removeEventListener("xpert:telemetry", _bridgeListener);
    _bridgeListener = null;
  }
  _initialized = false;
}

// Exposed for tests + an eventual devtools panel. Do NOT use in app code.
export const __test = { _teardown };
